<template>
  <v-tooltip
    v-if="!currentUser.isExternal"
    bottom
  >
    <template #activator="{ on, attrs }">
      <v-badge
        bordered
        bottom
        :class="badgeClass"
        :color="statusBadgeColor"
        :icon="statusIcon"
        offset-x="20"
        offset-y="20"
        overlay
        right
        :value="anyCalendarConfigured"
      >
        <v-btn
          icon
          v-bind="attrs"
          v-on="on"
          @click.prevent="_ => openGoogleCalendarSettingsPage()"
        >
          <v-icon>
            mdi-calendar-blank
          </v-icon>
        </v-btn>
      </v-badge>
    </template>

    {{ statusText }}
  </v-tooltip>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import { alertHandler } from '@/alertHandler'
import { subscribeToCalendarChannel } from '@/channels/calendars_channel'

import { GOOGLE_CALENDAR_NAMESPACE } from '@/store/google-calendar'
import { OUTLOOK_CALENDAR_NAMESPACE } from '@/store/outlook-calendar'

export default {
  name: 'CalendarSyncStatusButton',

  setup () {
    return {
      ...alertHandler(),
    }
  },

  computed: {
    anyAccessible () {
      return this.isGoogleCalendarAccessible || this.isOutlookCalendarAccessible
    },

    anyCalendarConfigured () {
      return this.isGoogleCalendarConfigured || this.isOutlookCalendarConfigured
    },

    anySyncing () {
      return this.isGoogleCalendarSyncing || this.isOutlookCalendarSyncing
    },

    badgeClass () {
      if (this.anySyncing) return ['small-badge', 'rotating-badge-icon']
      return 'small-badge'
    },

    statusBadgeColor () {
      if (!this.anyCalendarConfigured) return 'transparent'
      if (this.anySyncing) return 'primary'
      if (this.anyAccessible) return 'green'
      return 'red'
    },

    statusIcon () {
      if (!this.anyCalendarConfigured) return ''
      if (this.anySyncing) return 'mdi-sync'
      if (this.anyAccessible) return 'mdi-check'

      return 'mdi-exclamation-thick'
    },

    statusText () {
      if (!this.anyCalendarConfigured) {
        return 'Click to setup calendar synchronization.'
      }

      if (this.isGoogleCalendarSyncing) {
        return `Google Calendar Sync is in progress: ${this.googleCalendarSyncProgress.toFixed(2)}% completed`
      }

      if (this.isOutlookCalendarSyncing) {
        return `Outlook Calendar Sync is in progress: ${this.outlookCalendarSyncProgress.toFixed(2)}% completed`
      }

      if (this.anyAccessible) {
        return 'Your LL Tasks and Events are synced with your calendar'
      }

      if (this.isGoogleCalendarLinked || this.isOutlookCalendarLinked) {
        return 'There are issues accessing your calendar'
      }

      return 'Connection to your calendar has been lost. Please Relink your account'
    },

    ...mapGetters(GOOGLE_CALENDAR_NAMESPACE, [
      'googleCalendarSyncProgress',
      'isGoogleCalendarAccessible',
      'isGoogleCalendarConfigured',
      'isGoogleCalendarLinked',
      'isGoogleCalendarSyncing',
    ]),

    ...mapGetters(OUTLOOK_CALENDAR_NAMESPACE, [
      'isOutlookCalendarAccessible',
      'isOutlookCalendarConfigured',
      'isOutlookCalendarLinked',
      'isOutlookCalendarSyncing',
      'outlookCalendarSyncProgress',
    ]),

    ...mapGetters([
      'currentUser',
    ]),
  },

  created () {
    if (this.currentUser.isExternal) return

    this.refreshGoogleCalendarInfo().
      catch(_ => {
        this.displayAlert('error', 'We lost connection with your Google Calendar. You might want to relink it!')
      })

    this.refreshOutlookCalendarInfo().
      catch(_ => {
        this.displayAlert('error', 'We lost connection with your Outlook Calendar. You might want to relink it!')
      })

    subscribeToCalendarChannel({ onUpdate: this.processReceivedNotification })
  },

  methods: {
    openGoogleCalendarSettingsPage () {
      if (this.$route.name !== 'user-account-integration-settings') {
        this.$router.push({ name: 'user-account-integration-settings' })
      }
    },

    processReceivedNotification (notification) {
      if (notification.sync) {
        this.updateGoogleCalendarInfo(notification)
      }

      if (notification.outlook_sync) {
        this.updateOutlookCalendarInfo({ sync: notification.outlook_sync })
      }
    },

    ...mapActions(GOOGLE_CALENDAR_NAMESPACE, [
      'refreshGoogleCalendarInfo',
      'updateGoogleCalendarInfo',
    ]),

    ...mapActions(OUTLOOK_CALENDAR_NAMESPACE, [
      'refreshOutlookCalendarInfo',
      'updateOutlookCalendarInfo',
    ]),
  },
}
</script>

<style scoped>
.small-badge {
  .v-badge__badge {
    min-width: auto;
    width: 14px;
    height: 14px;
    line-height: 14px;
    padding: 0;

    .v-icon {
      font-size: 9px;
    }
  }
}

@-webkit-keyframes badge-icon-endless-rotation {
  from {
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

@keyframes badge-icon-endless-rotation {
  from {
    -ms-transform: rotate(0deg);
    -moz-transform: rotate(0deg);
    -webkit-transform: rotate(0deg);
    -o-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  to {
    -ms-transform: rotate(360deg);
    -moz-transform: rotate(360deg);
    -webkit-transform: rotate(360deg);
    -o-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

.rotating-badge-icon {
  .v-badge__badge {
    -webkit-animation: badge-icon-endless-rotation 2s linear infinite;
    -moz-animation: badge-icon-endless-rotation 2s linear infinite;
    -ms-animation: badge-icon-endless-rotation 2s linear infinite;
    -o-animation: badge-icon-endless-rotation 2s linear infinite;
    animation: badge-icon-endless-rotation 2s linear infinite;
  }
}
</style>
